import {Component, Input} from '@angular/core';
import {ControlContainer, FormControl, FormGroupDirective, ReactiveFormsModule} from "@angular/forms";
import {NgClass, NgIf} from "@angular/common";
import {InfoTooltipComponent} from "../../info-tooltip/info-tooltip.component";

// Fraction of the limit at which the counter turns amber and screen-reader announcements begin.
const NEAR_LIMIT_RATIO = 0.9;

// Remaining characters are announced rounded down to this step, and exactly once at or below it.
const ANNOUNCE_BUCKET = 10;

@Component({
  selector: 'app-input-field',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    NgIf,
    NgClass,
    InfoTooltipComponent
  ],
  templateUrl: './input-field.component.html',
  styleUrl: './input-field.component.scss',
  viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }]
})
export class InputFieldComponent {

  @Input() label: string = '';
  @Input() placeholder: string = '';
  @Input() controlName: string = '';
  @Input() required: boolean = false;
  @Input() showIcon: boolean = false;
  @Input() maxlength: number = 255;
  @Input() showCounter: boolean = false;
  @Input() type: string = 'text';
  @Input() pattern: string = '';
  @Input() patternErrorMessage: string = 'Please enter a valid input.';

  @Input() tooltip: string = '';
  @Input() tooltipPosition: string = 'right';
  @Input() tooltipClass: string = 'custom-tooltip';

  constructor(public controlContainer: FormGroupDirective) {}

  get formControl(): FormControl {
    return this.controlContainer.control.get(this.controlName) as FormControl;
  }

  get hasValue(): boolean {
    const value = this.formControl?.value;

    // Check for null, undefined, or empty string
    if (value === null || value === undefined || value === '') {
      return false;
    }

    // If it's a string, check if it's only whitespace
    if (typeof value === 'string' && value.trim() === '') {
      return false;
    }

    // If a pattern is provided, check if the value matches it
    if (this.pattern && typeof value === 'string') {
      const regex = new RegExp(this.pattern);
      return regex.test(value);
    }

    return true;
  }

  get characterCount(): number {
    const value = this.formControl?.value;

    return typeof value === 'string' ? value.length : 0;
  }

  get counterClass(): string {
    switch (this.limitState(this.characterCount)) {
      case 'over':
        return 'text-red-600 dark:text-red-400 font-medium';
      case 'near':
        return 'text-amber-600 dark:text-amber-400';
      default:
        return 'text-gray-500 dark:text-primary-100/70';
    }
  }

  // Carries no live count while far from the limit, and rounds down to whole tens above the last few characters, so
  // consecutive keystrokes usually produce an identical string and the live region stays quiet instead of announcing
  // every character typed.
  get counterAnnouncement(): string {
    const count = this.characterCount;
    const state = this.limitState(count);

    if (state === 'over') {
      return `${this.label} is over the ${this.maxlength} character limit.`;
    }

    if (state !== 'near') {
      return '';
    }

    const remaining = this.maxlength - count;

    if (remaining === 0) {
      return `Character limit reached for ${this.label}.`;
    }

    const announced = remaining <= ANNOUNCE_BUCKET
      ? remaining
      : Math.floor(remaining / ANNOUNCE_BUCKET) * ANNOUNCE_BUCKET;

    return `${announced} characters remaining for ${this.label}.`;
  }

  // Takes the count as an argument so a caller resolves the form control once instead of once per derived value.
  // 'over' is only reachable for values set programmatically — the native maxlength blocks typing and pasting.
  private limitState(count: number): 'over' | 'near' | 'ok' {
    if (count > this.maxlength) {
      return 'over';
    }

    return count >= this.maxlength * NEAR_LIMIT_RATIO ? 'near' : 'ok';
  }
}
